/**
 * Admin Raffle List Page - shows all raffles for an event
 */

import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layout } from "@/components/layout";
import { PageLoader } from "@/components/PageLoader";
import {
  IconPlus,
  IconGift,
  IconTicket,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconPlay,
  IconCopy,
  IconBeaker,
} from "@/components/Icons";
import { getTelegram, haptic } from "@/lib/telegram";
import {
  useAdminRaffles,
  useCreateRaffle,
  useCopyRaffle,
  type Raffle,
  type RaffleStatus,
} from "./hooks";

const STATUS_CONFIG: Record<
  RaffleStatus,
  { label: string; color: string; icon: React.ReactNode }
> = {
  DRAFT: {
    label: "Черновик",
    color: "bg-slate-100 text-slate-600",
    icon: <IconClock className="w-4 h-4" />,
  },
  READY: {
    label: "Готов",
    color: "bg-blue-100 text-blue-600",
    icon: <IconCheck className="w-4 h-4" />,
  },
  IN_PROGRESS: {
    label: "Идёт",
    color: "bg-amber-100 text-amber-600",
    icon: <IconPlay className="w-4 h-4" />,
  },
  COMPLETED: {
    label: "Завершён",
    color: "bg-green-100 text-green-600",
    icon: <IconCheck className="w-4 h-4" />,
  },
};

export function AdminRafflePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  const { raffles, isLoading, error, refetch } = useAdminRaffles(eventId ?? "");
  const { createRaffle, isLoading: isCreating } = useCreateRaffle();
  const { copyRaffle, isLoading: isCopying } = useCopyRaffle();

  // BackButton handling
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => navigate(`/admin/events/${eventId}`);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, eventId]);

  const handleCreate = async () => {
    if (!eventId) return;

    try {
      const result = await createRaffle(eventId, { name: "Новый розыгрыш" });
      if (result) {
        haptic("success");
        navigate(`/admin/raffle/${result}`);
      }
    } catch (err) {
      haptic("error");
    }
  };

  const handleRaffleClick = (raffle: Raffle) => {
    haptic("light");
    navigate(`/admin/raffle/${raffle.id}`);
  };

  const handleCopyRaffle = async (e: React.MouseEvent, raffle: Raffle, asTest: boolean = false) => {
    e.stopPropagation();
    
    try {
      const newId = await copyRaffle({ id: raffle.id, isTest: asTest });
      haptic("success");
      refetch();
      navigate(`/admin/raffle/${newId}`);
    } catch (err) {
      haptic("error");
    }
  };

  if (isLoading) {
    return <PageLoader message="Загрузка розыгрышей..." />;
  }

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <button onClick={() => refetch()} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Розыгрыши</h1>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="btn-primary flex items-center gap-2"
          >
            <IconPlus className="w-5 h-5" />
            <span>Создать</span>
          </button>
        </div>

        {/* Empty state */}
        {raffles.length === 0 && (
          <div className="card p-8 text-center">
            <IconGift className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-lg font-semibold text-slate-700 mb-2">
              Нет розыгрышей
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Создайте розыгрыш призов для этого мероприятия
            </p>
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="btn-primary"
            >
              Создать розыгрыш
            </button>
          </div>
        )}

        {/* Raffle list */}
        <div className="space-y-3">
          {raffles.map((raffle) => {
            const status = STATUS_CONFIG[raffle.status];
            return (
              <div
                key={raffle.id}
                className="card p-4 hover:bg-slate-50 active:bg-slate-100 transition-colors"
              >
                <button
                  onClick={() => handleRaffleClick(raffle)}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl ${raffle.isTest ? 'bg-orange-100 text-orange-600' : 'bg-purple-100 text-purple-600'}`}>
                      {raffle.isTest ? <IconBeaker className="w-6 h-6" /> : <IconGift className="w-6 h-6" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {raffle.name}
                        </h3>
                        {raffle.isTest && (
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-600">
                            Тест
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <IconGift className="w-4 h-4" />
                          {raffle.prizes.length} призов
                        </span>
                        <span className="flex items-center gap-1">
                          <IconTicket className="w-4 h-4" />
                          {raffle.ticketCount} билетов
                        </span>
                      </div>
                    </div>
                    <div
                      className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 ${status.color}`}
                    >
                      {status.icon}
                      {status.label}
                    </div>
                  </div>
                </button>
                
                {/* Copy actions - compact with label */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">
                    Создать копию:
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => handleCopyRaffle(e, raffle, false)}
                      disabled={isCopying}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
                      title="Создать обычную копию розыгрыша"
                    >
                      <IconCopy className="w-3.5 h-3.5" />
                      Обычную
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={(e) => handleCopyRaffle(e, raffle, true)}
                      disabled={isCopying}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                      title="Создать тестовую копию (не видна пользователям, можно сбросить)"
                    >
                      <IconBeaker className="w-3.5 h-3.5" />
                      Тестовую
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
